// src/lib/observability/LoggerContext.tsx
import { createContext, ReactNode, useContext, useMemo } from "react";
import { createLogger, Logger } from "./logger";
import { useCorrelationContext } from "./correlation-context";

// Default logger instance - enhanced with correlation IDs
const defaultLogger = createLogger("app");

// Context for the logger
interface LoggerContextType {
  logger: Logger;
  createComponentLogger: (componentName: string) => Logger;
}

const LoggerContext = createContext<LoggerContextType>({
  logger: defaultLogger,
  createComponentLogger: (componentName: string) => createLogger("app", componentName),
});

// Props for the LoggerProvider
interface LoggerProviderProps {
  serviceName?: string;
  children: ReactNode;
  userId?: string;
  organizationId?: string;
}

/**
 * Provider component that makes the logger available throughout the component tree
 */
export function LoggerProvider({
  serviceName = "web",
  children,
  userId,
  organizationId,
}: LoggerProviderProps) {
  // Get correlation context to pass to loggers
  const correlationContext = useCorrelationContext();

  // Create the main app logger with correlation support
  const appLogger = useMemo(() =>
    createLogger(serviceName),
    [serviceName]
  );

  // Create a function to generate component-specific loggers
  const createComponentLogger = useMemo(() => {
    return (componentName: string) => {
      // Create a new logger with the component name
      return createLogger(serviceName, componentName);
    };
  }, [serviceName]);

  // Create a context value
  const contextValue = useMemo(
    () => ({
      logger: appLogger,
      createComponentLogger,
    }),
    [appLogger, createComponentLogger]
  );

  // Log app initialization if this is the root logger
  if (serviceName === "web") {
    appLogger.info("Application initialized", {
      ...(userId ? { user_id: userId } : {}),
      ...(organizationId ? { organization_id: organizationId } : {}),
    });
  }

  return (
    <LoggerContext.Provider value={contextValue}>
      {children}
    </LoggerContext.Provider>
  );
}

/**
 * Custom hook to use the logger throughout the application
 * @param componentName Optional component name for component-specific logging
 * @returns An object with logging methods
 */
export function useLogger(componentName?: string) {
  const { logger, createComponentLogger } = useContext(LoggerContext);

  // Create a component-specific logger if a name is provided
  const componentLogger = useMemo(() => {
    if (componentName) {
      return createComponentLogger(componentName);
    }
    return logger;
  }, [logger, createComponentLogger, componentName]);

  // Return a simplified interface for component use
  return useMemo(
    () => ({
      // Core logging methods
      debug: (message: string, fields?: Record<string, unknown>) =>
        componentLogger.debug(message, fields),

      info: (message: string, fields?: Record<string, unknown>) =>
        componentLogger.info(message, fields),

      warn: (message: string, fields?: Record<string, unknown>) =>
        componentLogger.warn(message, fields),

      error: (message: string, error?: Error, fields?: Record<string, unknown>) =>
        componentLogger.error(message, error, fields),

      // Convenience method for errors with Error objects
      logError: (method: string, error: Error, fields?: Record<string, unknown>) =>
        componentLogger.logError(method, error, fields),

      // Raw logger access (for advanced usage)
      logger: componentLogger,
    }),
    [componentLogger]
  );
}

// For pages or components that need to enhance the logger with user context
export function useLoggerWithUserContext(
  componentName: string,
  userId?: string,
  organizationId?: string
) {
  const { createComponentLogger } = useContext(LoggerContext);

  return useMemo(() => {
    const logger = createComponentLogger(componentName);

    const userFields: Record<string, unknown> = {
      ...(userId ? { user_id: userId } : {}),
      ...(organizationId ? { organization_id: organizationId } : {}),
    };

    return {
      debug: (message: string, additionalFields?: Record<string, unknown>) =>
        logger.debug(message, { ...userFields, ...additionalFields }),

      info: (message: string, additionalFields?: Record<string, unknown>) =>
        logger.info(message, { ...userFields, ...additionalFields }),

      warn: (message: string, additionalFields?: Record<string, unknown>) =>
        logger.warn(message, { ...userFields, ...additionalFields }),

      error: (message: string, error?: Error, additionalFields?: Record<string, unknown>) =>
        logger.error(message, error, { ...userFields, ...additionalFields }),

      logError: (method: string, error: Error, additionalFields?: Record<string, unknown>) =>
        logger.logError(method, error, { ...userFields, ...additionalFields }),

      logger,
    };
  }, [createComponentLogger, componentName, userId, organizationId]);
}
